import {useState} from 'react'
import {
  Edit2,
  Trash2,
  Lock,
  ChevronDown,
  Shield,
  CheckCircle,
  Bot,
  AlertTriangle,
} from 'lucide-react'
import {
  getCategoryLabel,
  getPermissionCategory,
  getPermissionRisk,
  isAgentStudioPermission,
  type Permission,
  type PermissionId,
} from './permissionMetadata'

type PermissionCardProps = {
  group: {
    name: string
    data: PermissionId[]
  }
  permissions: Permission[]
  onEdit: (group: any) => void
  onDelete: (group: any) => void
  isExpanded?: boolean
  onToggleExpand?: () => void
}

export const PermissionCard = ({
  group,
  permissions,
  onEdit,
  onDelete,
  isExpanded: externalIsExpanded,
  onToggleExpand,
}: PermissionCardProps) => {
  const [internalIsExpanded, setInternalIsExpanded] = useState(true)

  const isExpanded = externalIsExpanded !== undefined ? externalIsExpanded : internalIsExpanded

  const toggleExpand = () => {
    if (onToggleExpand) {
      onToggleExpand()
    } else {
      setInternalIsExpanded(!internalIsExpanded)
    }
  }

  const permissionMap = new Map(permissions.map((p) => [p.id_feature, p]))

  const groupedPermissions = group.data.reduce((acc, permissionId) => {
    const permission = permissionMap.get(permissionId)
    if (!permission) return acc

    const category = getPermissionCategory(permission.name)
    if (!acc[category]) {
      acc[category] = []
    }
    acc[category].push(permission)
    return acc
  }, {} as Record<string, Permission[]>)

  const totalPermissions = group.data.length
  const categoryCount = Object.keys(groupedPermissions).length
  const resolvedPermissions = Object.values(groupedPermissions).flat()
  const agentStudioCount = resolvedPermissions.filter((permission) =>
    isAgentStudioPermission(permission)
  ).length
  const sensitiveCount = resolvedPermissions.filter(
    (permission) => getPermissionRisk(permission.name) !== 'standard'
  ).length
  const hasAgentStudio = agentStudioCount > 0

  return (
    <div className='bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden transition-all duration-200 hover:shadow-md'>
      <button
        onClick={toggleExpand}
        className='w-full bg-gradient-to-r  p-6 border-b border-gray-200 hover:from-blue-100 hover:to-indigo-100 transition-all duration-200'
      >
        <div className='flex items-center justify-between'>
          <div className='flex items-center space-x-4'>
            <div className='p-3 bg-white rounded-xl shadow-sm'>
              <Shield className='w-6 h-6 text-blue-600' />
            </div>
            <div className='text-left'>
              <h3 className='text-xl font-bold text-gray-900'>{group.name}</h3>
              <div className='mt-2 flex flex-wrap items-center gap-2'>
                <span className='inline-flex items-center gap-1.5 px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-semibold'>
                  <CheckCircle className='w-3.5 h-3.5' />
                  {totalPermissions} {totalPermissions === 1 ? 'permissão' : 'permissões'}
                </span>
                <span className='inline-flex items-center gap-1.5 px-3 py-1 bg-purple-100 text-purple-700 rounded-full text-xs font-semibold'>
                  <Lock className='w-3.5 h-3.5' />
                  {categoryCount} {categoryCount === 1 ? 'categoria' : 'categorias'}
                </span>
                {hasAgentStudio && (
                  <span className='inline-flex items-center gap-1.5 rounded-full bg-blue-100 px-3 py-1 text-xs font-semibold text-blue-700'>
                    <Bot className='h-3.5 w-3.5' /> {agentStudioCount} Agent Studio
                  </span>
                )}
                {sensitiveCount > 0 && (
                  <span className='inline-flex items-center gap-1.5 rounded-full bg-amber-100 px-3 py-1 text-xs font-semibold text-amber-700'>
                    <AlertTriangle className='h-3.5 w-3.5' /> {sensitiveCount} sensíveis
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className='flex items-center gap-3'>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onEdit(group)
              }}
              className='p-2.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition-all duration-200 hover:scale-110'
              title='Editar grupo'
            >
              <Edit2 className='w-5 h-5' />
            </button>
            <button
              onClick={(e) => {
                e.stopPropagation()
                onDelete(group)
              }}
              className='p-2.5 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-all duration-200 hover:scale-110'
              title='Excluir grupo'
            >
              <Trash2 className='w-5 h-5' />
            </button>
            <div
              className={`transform transition-transform duration-200 ml-2 ${
                isExpanded ? 'rotate-0' : '-rotate-90'
              }`}
            >
              <ChevronDown className='w-6 h-6 text-gray-600' />
            </div>
          </div>
        </div>
      </button>

      <div
        className={`transition-all duration-300 ease-in-out overflow-hidden ${
          isExpanded ? 'max-h-[3000px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className='p-6 space-y-6'>
          {Object.entries(groupedPermissions)
            .sort(([a], [b]) =>
              a === 'agent-studio'
                ? -1
                : b === 'agent-studio'
                ? 1
                : getCategoryLabel(a).localeCompare(getCategoryLabel(b), 'pt-BR')
            )
            .map(([category, perms]) => (
              <div key={category} className='space-y-3'>
                <div className='flex items-center gap-2 pb-2 border-b border-gray-200'>
                  <div className='w-1 h-5 bg-blue-500 rounded-full'></div>
                  <h4 className='text-sm font-bold text-gray-700 uppercase tracking-wide'>
                    {getCategoryLabel(category)}
                  </h4>
                  <span className='text-xs text-gray-500 ml-auto'>
                    {perms.length} {perms.length === 1 ? 'item' : 'itens'}
                  </span>
                </div>
                <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3'>
                  {perms.map((permission) => (
                    <div
                      key={permission.id_feature}
                      className='flex items-center space-x-3 p-3 rounded-lg bg-gradient-to-r bg-gray-100 border border-gray-200 hover:border-blue-300 transition-all duration-200 hover:shadow-sm'
                    >
                      <div className='flex-shrink-0'>
                        <CheckCircle className='w-4 h-4 text-green-500' />
                      </div>
                      <span className='text-sm font-medium text-gray-700 flex-1'>
                        {permission.title}
                      </span>
                      {getPermissionRisk(permission.name) !== 'standard' && (
                        <span
                          className={
                            'rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ' +
                            (getPermissionRisk(permission.name) === 'critical'
                              ? 'bg-red-100 text-red-700'
                              : 'bg-amber-100 text-amber-700')
                          }
                        >
                          {getPermissionRisk(permission.name) === 'critical'
                            ? 'Crítica'
                            : 'Sensível'}
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
        </div>
      </div>
    </div>
  )
}
